import { type File, useStores } from '@littlebird/sdk';
import { useState } from 'react';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';
import { useTheme } from '@/providers/ThemeProvider';
import { inDesktop } from '@/utils/capabilities';
import { saveBlob } from '@/utils/file-io';

import { exportCanvas } from './canvas-export';
import { readCanvasHostTheme } from './canvas-theme';

import type { CanvasExportFormat, Theme } from '../../../electron/shared';

export const useCanvasExport = (file: File) => {
  const { files: filesStore } = useStores();
  const { theme } = useTheme();
  const [isExporting, setIsExporting] = useState(false);

  const runExport = async (format: CanvasExportFormat, mode?: Theme) => {
    if (!inDesktop()) {
      toast.error('Open the Littlebird desktop app to download this Canvas.');
      return;
    }

    setIsExporting(true);
    try {
      const fileUrl = await filesStore.getFileUrl(file);
      if (!fileUrl) {
        toast.error('Could not open the canvas to export it');
        return;
      }
      const response = await fetch(fileUrl);
      if (!response.ok) {
        toast.error('Could not open the canvas to export it');
        return;
      }
      const outcome = await exportCanvas({
        canvasText: await response.text(),
        hostTheme: readCanvasHostTheme(mode ?? theme),
        format,
        filenameSuffix: mode,
      });
      if (!outcome.ok) {
        toast.error(`Could not export the canvas: ${outcome.error}`);
        return;
      }
      await saveBlob(outcome.blob, outcome.filename);
    } catch (error) {
      logError(error, 'Failed to export canvas', {
        context: 'useCanvasExport',
        metadata: { fileId: file.id, format, mode },
      });
      toast.error('Could not export the canvas');
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, runExport };
};
